import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import Price from '../../Price';
import Radio from '../../Radio';
import SubscriptionInfo from '../../../assets/icons/SubscriptionInfo';

import renderIcon from '../../../utils/renderIcon';
import { getSellingPlanGroupsByProductId } from './helpers';

const ns = `product-subscriptions`;

const Subscriptions = ({
	product,
	variant,
	setSelectedSubscriptionPlan,
	selectedSubscriptionPlan,
	globalSettings
}) => {
	const [subscriptionPlans, setSubscriptionPlans] = useState([]);
	const [selectedOption, setSelectedOption] = useState('one-time');
	const { shopifyId, priceRange: { minVariantPrice: price } } = product
	
	const getDiscountPrice = () => {
		const subscriptionPlan = selectedSubscriptionPlan || subscriptionPlans?.[0]
		const priceNum = Number(variant?.price);
		
		if (!variant?.price || !subscriptionPlan?.discount_amount) return null;

		return priceNum - priceNum * (parseInt(subscriptionPlan.discount_amount, 10) / 100);
	}

	// Toggles subscription radio and calls setSelectedSubscriptionPlan
	// with the correct selling plan id, or null if 'One-Time Purchase' is selected
	const handleOptionChange = e => {
		const { target } = e;
		setSelectedOption(target.value);

		if (target.value === 'one-time') {
			setSelectedSubscriptionPlan(null);
		} else {
			setSelectedSubscriptionPlan(subscriptionPlans[0]);
		}
	};

	// Sets the current value for subscription frequency and updates the
	// selected selling plan id (passed up and into add to cart component)
	const handleFreqChange = e => {
		const selected = e.target.value;

		for (let i = 0; i < subscriptionPlans.length; i += 1) {
			const currentPlan = subscriptionPlans[i];

			if (currentPlan?.external_plan_id.toString() === selected) {
				setSelectedSubscriptionPlan(currentPlan);
				break;
			}
		}
	};

	const renderPlanFreqText = plan => {
		if (typeof plan !== 'object') return '';

		const {
			subscription_preferences: {
				order_interval_frequency: intervalFrequency,
				interval_unit: intervalUnit,
			},
		} = plan;
		const intervalUnitPluralized = intervalFrequency !== 1 ? `${ intervalUnit }s` : intervalUnit;

		return `Every ${ intervalFrequency } ${ intervalUnitPluralized }`;
	};

	const renderSubscriptinOptionText = plans => {
		let discount = plans[0].discount_amount || false;

		if (discount) {
			discount = parseInt(discount, 10);
		}

		return discount ? `Subscribe & Save ${ discount }%` : 'Subscribe';
	};

	// Fetch the product's selling plans
	useEffect(() => {
		if (!shopifyId) return;
		const productId = shopifyId?.split('/').pop();

		getSellingPlanGroupsByProductId(productId)
			.then((p = []) => {
				const plans = p || [];
				// If the Recharge Subscriptions app was installed prior to 2020
				// each plan object will not have an external_plan_id
				const [first] = plans;
				if (first?.external_plan_id) {
					return setSubscriptionPlans(plans.reverse());
				}
				return setSubscriptionPlans(plans);
			})
			.catch(err => {
				setSubscriptionPlans([]);
				// eslint-disable-next-line
				console.error('Error', err);
			});
	}, [shopifyId]);

	// Pre-select first item in plans array by default
	useEffect(() => {
		if (selectedOption === 'one-time') return;

		setSelectedSubscriptionPlan(subscriptionPlans[0]);
	}, [subscriptionPlans, setSelectedSubscriptionPlan, selectedOption]);

	const rootClassnames = classNames({
		[`${ ns }`]: true,
	});

	if (!subscriptionPlans.length > 0) return null;

	return (
		<div className={rootClassnames}>
			<div className={`${ ns }__options`}>
				<div className={`${ ns }__option`}>
					<div className={`${ ns }__option-row`}>					
						<Radio
							label={'One-time Purchase'}
							name={'subscription-option'}
							value={'one-time'}
							checked={selectedOption === 'one-time'}
							onChange={handleOptionChange}
						/>
						<Price
							currencyCode={price?.currencyCode}
							price={variant?.price}
							compareAtPrice={variant?.compareAtPrice}
						/>
					</div>
				</div>
				<div className={`${ ns }__option`}>
					<div className={`${ ns }__option-row`}>
						<Radio
							label={renderSubscriptinOptionText(subscriptionPlans)}
							name={'subscription-option'}
							value={'recurring'}
							checked={selectedOption === 'recurring'}
							onChange={handleOptionChange}
						/>
						{getDiscountPrice() && (
							<Price
								currencyCode={price?.currencyCode}
								price={getDiscountPrice()}
								compareAtPrice={variant?.price}
							/>
						)}
					</div>
					<div className={`${ ns }__option-row ${ ns }__option-row-disclaimer`}>
						<a
							href={globalSettings.rechargeSeeDetailsUrl}
							target={'_blank'}
							rel={'noreferrer'}
						>
							<SubscriptionInfo /> Subscription details
						</a>
						<p>Cancel Anytime</p>
					</div>
				</div>
			</div>
			{selectedOption === 'recurring' && (
				<div className={`${ ns }__freq`}>
					<div className={`${ ns }__freq-title text-small`}>DELIVERY FREQUENCY</div>
					<label htmlFor={'subscription-frequency'} className={`${ ns }__freq-wrap`}>
						<div className={`${ns}__freq-select`}>
							<span className={`${ ns }__freq-value`}>
								{renderPlanFreqText(selectedSubscriptionPlan)}
							</span>
							<span aria-hidden>
								{renderIcon('cheveron-down')}
							</span>
							<select
								id={'subscription-frequency'}
								name={'subscription-frequency'}
								className={`${ ns }__freq-options`}
								onChange={handleFreqChange}
							>
								{subscriptionPlans.map((
									{external_plan_id: planId, ...plan}
								) => {
									return (
										<option key={planId || plan?.id} value={planId}>
											{renderPlanFreqText(plan)}
										</option>
									);
								})}
							</select>
						</div>
					</label>
				</div>
			)}
		</div>
	);
};

export default Subscriptions;
